import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, School } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function SchoolAuth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ school_name: "", email: "", password: "", year_system: "scottish" });
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const forgotSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email: form.email, role: "school", origin_url: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  const [busy, setBusy] = useState(false);
  const { saveSession } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = mode === "login"
        ? await api.post("/auth/school/login", { email: form.email, password: form.password })
        : await api.post("/auth/school/register", form);
      saveSession(data);
      toast.success(mode === "login" ? `Welcome back, ${data.user.name}!` : "Your school is registered!");
      navigate("/admin");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="school-auth-page">
      <div className="hidden lg:block relative border-r-2 border-zinc-900">
        <img src="https://images.unsplash.com/photo-1543269865-cbf427effbad" alt="Students collaborating" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1E3A8A]/70 flex flex-col justify-end p-12">
          <h2 className="font-display text-4xl font-black text-white leading-tight">Run your whole school week from one dashboard.</h2>
          <p className="text-blue-100 mt-4 max-w-md">Schedule layouts, teacher & room directories, pupil accounts and school-wide notices.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10" data-testid="auth-brand">
            <div className="w-9 h-9 rounded-xl border-2 border-zinc-900 bg-[#A7E4D3] flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <Clock className="w-5 h-5" />
            </div>
            <span className="font-display font-black text-2xl">MyTimetable.co.uk</span>
          </Link>

          <div className="nb-card p-8">
            <div className="flex rounded-xl border-2 border-zinc-900 overflow-hidden mb-8" role="tablist">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  type="button"
                  role="tab"
                  data-testid={`school-${m}-tab`}
                  onClick={() => setMode(m)}
                  className={`flex-1 py-2.5 font-display font-bold text-sm transition-colors duration-150 ${mode === m ? "bg-[#1E3A8A] text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                >
                  {m === "login" ? "School login" : "Register school"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="space-y-5">
              {mode === "register" && (
                <div>
                  <label className="nb-label" htmlFor="school-name">School name</label>
                  <input id="school-name" data-testid="school-name-input" className="nb-input mt-2" placeholder="Airdrie Academy"
                    value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} required minLength={2} />
                </div>
              )}
              {mode === "register" && (
                <div>
                  <label className="nb-label" htmlFor="school-year-system">Year group naming</label>
                  <select id="school-year-system" className="nb-input mt-2" data-testid="school-year-system-select"
                    value={form.year_system} onChange={(e) => setForm({ ...form, year_system: e.target.value })}>
                    <option value="scottish">Scottish — 1st to 6th Year (default)</option>
                    <option value="english">English — Year 7 to 13</option>
                    <option value="college">College / adult education — Year 1 to 4</option>
                  </select>
                  <p className="text-xs text-zinc-400 font-semibold mt-1">You can rename or edit year groups any time in Settings.</p>
                </div>
              )}
              <div>
                <label className="nb-label" htmlFor="school-email">Admin email</label>
                <input id="school-email" type="email" data-testid="school-email-input" className="nb-input mt-2" placeholder="office@school.sch.uk"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div>
                <label className="nb-label" htmlFor="school-password">Password</label>
                <input id="school-password" type="password" data-testid="school-password-input" className="nb-input mt-2" placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={mode === "register" ? 8 : 1} />
              </div>
              <button type="submit" disabled={busy} data-testid="school-auth-submit" className="nb-btn w-full bg-[#1E3A8A] text-white py-3">
                <School className="w-4 h-4" />
                {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create school account"}
              </button>
            </form>
            {mode === "login" && !showForgot && (
              <button type="button" onClick={() => setShowForgot(true)} data-testid="school-forgot-link"
                className="w-full text-center text-xs font-bold text-zinc-500 hover:text-zinc-900 mt-4 transition-colors duration-150">
                Forgot your password?
              </button>
            )}
            {mode === "login" && showForgot && (
              <div className="mt-4 pt-4 border-t-2 border-zinc-100" data-testid="school-forgot-form">
                {forgotSent ? (
                  <p className="text-sm font-bold text-zinc-600 text-center" data-testid="school-forgot-sent">If an account exists for that email, a reset link is on its way. It lasts 1 hour.</p>
                ) : (
                  <form onSubmit={forgotSubmit} className="flex gap-2">
                    <input type="email" className="nb-input flex-1" data-testid="school-forgot-email" placeholder="Your admin email"
                      value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                    <button type="submit" data-testid="school-forgot-submit" className="nb-btn bg-[#FDE68A] px-4 py-2 text-sm shrink-0">Email reset link</button>
                  </form>
                )}
              </div>
            )}
          </div>

          <p className="text-center text-sm text-zinc-500 mt-6">
            A pupil? <Link to="/pupil/auth" className="font-bold text-[#1E3A8A] underline" data-testid="goto-pupil-auth">Use your join code</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
